import { OPENHASP_CONTROL_TYPE } from './controlTypes.js'

function decimalPlaces(value) {
    const text = String(value).toLowerCase()
    const [mantissa, exponentText] = text.split('e')
    const mantissaPlaces = (mantissa.split('.')[1] || '').length
    const exponent = Number(exponentText || 0)
    return Math.max(0, mantissaPlaces - exponent)
}

function roundDecimal(value, precision) {
    const places = Math.min(15, Math.max(0, precision + 4))
    return Number(value.toFixed(places))
}

/**
 * Quantize a Bar/Slider value from the configured minimum. A zero, missing or
 * invalid step deliberately keeps the old continuous-value behaviour.
 */
export function quantizeBarValue(value, min, max, step = 0) {
    const low = Number(min)
    const high = Number(max)
    if (!Number.isFinite(low) || !Number.isFinite(high) || high < low) return null

    const numericValue = Number(value)
    const current = Number.isFinite(numericValue) ? numericValue : low
    const increment = Number(step)
    if (!Number.isFinite(increment) || increment <= 0) {
        return Math.max(low, Math.min(high, current))
    }

    const precision = Math.max(
        decimalPlaces(low),
        decimalPlaces(high),
        decimalPlaces(increment),
        decimalPlaces(current),
    )
    const ratio = (current - low) / increment
    // Decimal inputs such as 0.35 / 0.1 can land infinitesimally below a
    // half-step in binary floating point. Keep the requested round() rule
    // while making exact decimal half-steps behave predictably.
    const epsilon = Number.EPSILON * Math.max(1, Math.abs(ratio)) * 8
    const quantized = low + Math.round(ratio + epsilon) * increment
    return Math.max(low, Math.min(high, roundDecimal(quantized, precision)))
}

/**
 * Normalize the two values of an openHASP Range while preserving its
 * invariant: min <= start_value <= val <= max.
 *
 * Returning both values together makes the non-atomic nature of two separate
 * wire property writes explicit to callers and tests.
 */
export function normalizeRangeValues({ min, max, value, startValue, step = 0 }) {
    const low = Number(min)
    const high = Number(max)
    if (!Number.isFinite(low) || !Number.isFinite(high) || high < low) return null

    const normalizedValue = quantizeBarValue(value, low, high, step)
    const normalizedStart = Math.max(low, Math.min(normalizedValue,
        quantizeBarValue(startValue, low, high, step)))
    return { value: normalizedValue, startValue: normalizedStart }
}

/**
 * Resolve the LVGL 7 bar indicator rectangle along its primary axis.
 *
 * The returned `start` is measured from the visual top/left edge and `size`
 * is the filled length.  LVGL bars fill left-to-right horizontally and
 * bottom-to-top vertically in normal mode; reverse swaps that visual origin.
 */
export function lvglBarIndicatorGeometry({
    type = 0,
    min = 0,
    max = 100,
    value = 0,
    startValue = min,
    length = 0,
    horizontal = true,
} = {}) {
    const low = Number(min)
    const high = Number(max)
    const axisLength = Math.max(0, Number(length) || 0)

    if (!Number.isFinite(low) || !Number.isFinite(high) || high === low) {
        return { start: 0, size: 0 }
    }

    const current = Math.max(low, Math.min(high, Number(value)))
    const configuredStart = Number(startValue)
    const rangeStart = Math.max(low, Math.min(current, Number.isFinite(configuredStart) ? configuredStart : low))
    const fraction = (current - low) / (high - low)
    const startFraction = (rangeStart - low) / (high - low)
    const valuePosition = horizontal
        ? fraction * axisLength
        : axisLength - fraction * axisLength

    // LVGL_SYMMETRICAL only takes effect when the range crosses zero.  For
    // ranges such as 0..100, LVGL deliberately falls back to normal mode.
    const symmetrical = Number(type) === OPENHASP_CONTROL_TYPE.SYMMETRICAL && low < 0 && high > 0
    if (symmetrical) {
        const zeroFraction = (0 - low) / (high - low)
        const zeroPosition = horizontal
            ? zeroFraction * axisLength
            : axisLength - zeroFraction * axisLength
        return {
            start: Math.min(valuePosition, zeroPosition),
            size: Math.abs(valuePosition - zeroPosition),
        }
    }

    if (Number(type) === OPENHASP_CONTROL_TYPE.REVERSE) {
        if (horizontal) {
            return { start: axisLength - fraction * axisLength, size: fraction * axisLength }
        }
        return { start: 0, size: fraction * axisLength }
    }

    if (Number(type) === OPENHASP_CONTROL_TYPE.RANGE) {
        if (horizontal) return { start: startFraction * axisLength, size: (fraction - startFraction) * axisLength }
        return { start: valuePosition, size: (startFraction - fraction) * axisLength }
    }

    if (horizontal) return { start: 0, size: fraction * axisLength }
    return { start: valuePosition, size: axisLength - valuePosition }
}

const LVGL_BAR_SIZE_MIN = 4

function integer(value, fallback = 0) {
    const number = Number(value)
    return Number.isFinite(number) ? Math.trunc(number) : fallback
}

function resolvedPadding(padding = {}) {
    return {
        left: integer(padding.left),
        right: integer(padding.right),
        top: integer(padding.top),
        bottom: integer(padding.bottom),
    }
}

/**
 * Return LVGL's inclusive maximum indicator area after applying BG padding.
 *
 * This intentionally returns inclusive x2/y2 coordinates. LVGL 7 computes
 * the value offsets from `lv_area_get_width/height()` and has a deliberate
 * one-pixel +1 in the vertical branch. Keeping the source representation here
 * avoids silently turning that firmware behaviour into CSS-like half-open
 * geometry in the preview.
 */
export function lvglBarMaximumArea({ width = 0, height = 0, padding = {}, horizontal = true } = {}) {
    const objectWidth = Math.max(0, integer(width))
    const objectHeight = Math.max(0, integer(height))
    const { left, right, top, bottom } = resolvedPadding(padding)
    const area = {
        x1: left,
        y1: top,
        x2: objectWidth - 1 - right,
        y2: objectHeight - 1 - bottom,
    }

    // lv_bar.c centres a too-short indicator on the object, not on the
    // padded area. This is LV_BAR_SIZE_MIN's exact integer arithmetic.
    if (horizontal && area.y2 - area.y1 + 1 < LVGL_BAR_SIZE_MIN) {
        area.y1 = Math.trunc(objectHeight / 2) - Math.trunc(LVGL_BAR_SIZE_MIN / 2)
        area.y2 = area.y1 + LVGL_BAR_SIZE_MIN - 1
    }
    if (!horizontal && area.x2 - area.x1 + 1 < LVGL_BAR_SIZE_MIN) {
        area.x1 = Math.trunc(objectWidth / 2) - Math.trunc(LVGL_BAR_SIZE_MIN / 2)
        area.x2 = area.x1 + LVGL_BAR_SIZE_MIN - 1
    }
    return area
}

function areaLength(area, horizontal) {
    return horizontal
        ? area.x2 - area.x1 + 1
        : area.y2 - area.y1 + 1
}

function areaToBounds(area) {
    return {
        x: area.x1,
        y: area.y1,
        width: Math.max(0, area.x2 - area.x1 + 1),
        height: Math.max(0, area.y2 - area.y1 + 1),
    }
}

/**
 * LVGL 7.11's actual Bar indicator area, including BG padding and the
 * openHASP Reverse mirror. `type` uses the public openHASP values.
 */
export function lvglBarIndicatorBounds({
    type = OPENHASP_CONTROL_TYPE.NORMAL,
    min = 0,
    max = 100,
    value = 0,
    startValue = min,
    width = 0,
    height = 0,
    horizontal = true,
    padding = {},
} = {}) {
    const low = Number(min)
    const high = Number(max)
    const objectWidth = Math.max(0, integer(width))
    const objectHeight = Math.max(0, integer(height))
    const maximumArea = lvglBarMaximumArea({ width: objectWidth, height: objectHeight, padding, horizontal })

    if (!Number.isFinite(low) || !Number.isFinite(high) || high === low) {
        return { ...areaToBounds(maximumArea), maximum: areaToBounds(maximumArea), area: maximumArea, maximumArea, empty: true }
    }

    const current = Math.max(low, Math.min(high, Number(value)))
    const configuredStart = Number(startValue)
    const rangeStart = Math.max(low, Math.min(current, Number.isFinite(configuredStart) ? configuredStart : low))
    const range = high - low
    const length = areaLength(maximumArea, horizontal)
    const currentOffset = Math.trunc(length * (current - low) / range)
    const startOffset = Math.trunc(length * (rangeStart - low) / range)
    const area = { ...maximumArea }

    if (horizontal) {
        area.x2 = area.x1 + currentOffset
        area.x1 += startOffset
    } else {
        area.y1 = area.y2 - currentOffset + 1
        area.y2 -= startOffset
    }

    const symmetrical = Number(type) === OPENHASP_CONTROL_TYPE.SYMMETRICAL
        && low < 0 && high > 0 && rangeStart === low
    if (symmetrical) {
        const zeroOffset = Math.trunc((-low) * length / range)
        if (horizontal) {
            const zero = maximumArea.x1 + zeroOffset
            if (area.x2 > zero) area.x1 = zero
            else {
                area.x1 = area.x2
                area.x2 = zero
            }
        } else {
            const zero = maximumArea.y2 - zeroOffset + 1
            if (area.y1 < zero) area.y2 = zero
            else {
                area.y2 = area.y1
                area.y1 = zero
            }
        }
    } else if (Number(type) === OPENHASP_CONTROL_TYPE.REVERSE) {
        // openHASP's reverse renderer mirrors LVGL's normal area across the
        // same padded maximum area; this is not a CSS direction flip.
        if (horizontal) {
            const x1 = maximumArea.x1 + (maximumArea.x2 - area.x2)
            const x2 = maximumArea.x1 + (maximumArea.x2 - area.x1)
            area.x1 = x1
            area.x2 = x2
        } else {
            const y1 = maximumArea.y1 + (maximumArea.y2 - area.y2)
            const y2 = maximumArea.y1 + (maximumArea.y2 - area.y1)
            area.y1 = y1
            area.y2 = y2
        }
    }

    const empty = !symmetrical && areaLength(area, horizontal) <= 1
    const bounds = empty
        ? { x: horizontal ? area.x1 : maximumArea.x1, y: horizontal ? maximumArea.y1 : area.y1, width: horizontal ? 0 : areaToBounds(maximumArea).width, height: horizontal ? areaToBounds(maximumArea).height : 0 }
        : areaToBounds(area)
    return { ...bounds, maximum: areaToBounds(maximumArea), area, maximumArea, empty }
}
